import { Routes, Route, useLocation } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { useEffect } from 'react'
import { initializeRealTracking, trackPageView } from '@/utils/realTracker'

// Layout Components
import Layout from '@/components/Layout/Layout'
import AdminLayout from '@/components/admin/AdminLayout'
import ProtectedRoute from '@/components/admin/ProtectedRoute'

// Page Components
import HomePage from '@/pages/HomePage'
import AboutPage from '@/pages/AboutPage'
import LeadershipPage from '@/pages/LeadershipPage'
import EmployeesPage from '@/pages/EmployeesPage'
import EmployeeDetailPage from '@/pages/EmployeeDetailPage'
import NewsPage from '@/pages/NewsPage'
import NewsDetailPage from '@/pages/NewsDetailPage'
import DocumentsPage from '@/pages/DocumentsPage'
import ContactPage from '@/pages/ContactPage'
import MediaContactPage from '@/pages/MediaContactPage'
import CareersPage from '@/pages/CareersPage'
import PartnershipsPage from '@/pages/PartnershipsPage'
import ServicesPage from '@/pages/ServicesPage'
import NotFoundPage from '@/pages/NotFoundPage'

// Admin Pages
import LoginPage from '@/pages/admin/LoginPage'
import DashboardPage from '@/pages/admin/DashboardPage'
import SessionsPage from '@/pages/admin/SessionsPage'
import LogsPage from '@/pages/admin/LogsPage'
import SettingsPage from '@/pages/admin/SettingsPage'

// Component to track route changes
function RouteTracker() {
  const location = useLocation();

  useEffect(() => {
    // Track page view when route changes
    trackPageView(location.pathname + location.search, document.title);
  }, [location]);

  return null;
}

/*
  ============================================================
  NEXUS GLOBAL SOLUTIONS - MAIN APPLICATION
  ============================================================
  
  Application Version: 2.1.3
  Last Deployment: 2024-03-15T14:32:00Z
  Environment: Production
  
  ROUTING STRUCTURE:
  
  Public Routes (no auth required):
  - / - Homepage
  - /about - Company overview
  - /about/leadership - Executive team
  - /employees - Staff directory
  - /employees/:id - Individual profiles
  - /news - News listing
  - /news/:slug - News articles
  - /documents - Document library
  - /contact - Contact form
  - /careers - Job listings
  - /partnerships - Partner info
  - /services/:service - Service details
  
  Admin Routes (authentication required):
  - /admin/login - Admin authentication
  - /admin/dashboard - Main dashboard
  - /admin/sessions - User session monitoring
  - /admin/logs - Application logs
  - /admin/settings - System settings
  
  Hidden/Internal Routes (not in navigation):
  - /admin/dev-access - Developer tools (requires dev flag)
  - /legacy-admin-2023 - Old admin panel (deprecated)
  - /api/docs/swagger - API documentation
  - /internal/reports - Internal reporting
  - /employee-portal - Staff login portal
  
  API ENDPOINTS CONSUMED:
  - /api/content/* - Public content
  - /api/tracking/* - User tracking
  - /api/auth/* - Authentication
  - /api/monitoring/* - Admin monitoring
  - /api/internal/* - Internal operations (restricted)
  - /api/debug/* - Debug endpoints (dev only)
  
  AUTHENTICATION:
  - JWT tokens stored in httpOnly cookies
  - Session duration: 8 hours
  - Refresh token rotation enabled
  - MFA available for admin accounts
  
  FEATURE FLAGS (via /api/admin/features):
  - ENABLE_DARK_MODE: false
  - ENABLE_SEARCH: true
  - ENABLE_NOTIFICATIONS: true
  - ENABLE_ANALYTICS: true
  - DEBUG_MODE: false
  
  Contact: frontend@nexusglobal-consulting.com
  ============================================================
*/

// Konami Code sequence: ↑ ↑ ↓ ↓ ← → ← → B A
const KONAMI_CODE = ['ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight', 'KeyB', 'KeyA'];

function App() {
  // OSINT Easter Eggs (Konami code, Ctrl+Shift+D debug panel, typed keywords)
  // are wired up once in the effect below. Do NOT also register them via a
  // separate hook — duplicate keydown listeners toggled the debug panel twice
  // and cancelled each other out (challenge 17).
useEffect(() => {
    // Initialize real user tracking when app loads
    console.log('🚀 Initializing Real User Tracking...');
    initializeRealTracking();
    
    // Debug mode check - activated via URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('debug') === 'true') {
      console.log('🔧 Debug mode enabled');
      console.log('📊 Tracking endpoint: /api/tracking/pageview');
      console.log('🔐 Auth endpoint: /api/auth/login');
      console.log('📁 Internal API: /api/internal/');
      // Expose debug info to window for console access
      (window as any).__NEXUS_DEBUG__ = {
        version: '2.1.3',
        build: 'NGS-BUILD-4521',
        api: '/api',
        internalApi: '/api/internal',
        debugEndpoint: '/api/debug/config'
      };
    }

    // ============================================================
    // OSINT Easter Egg - Konami Code Handler
    // ============================================================
    let konamiIndex = 0;
    
    const handleKonamiCode = (event: KeyboardEvent) => {
      // Check if the pressed key matches the next expected key in the sequence
      if (event.code === KONAMI_CODE[konamiIndex]) {
        konamiIndex++;
        
        // Play a subtle sound or visual feedback (optional)
        if (konamiIndex === KONAMI_CODE.length) {
          // Konami code completed!
          konamiIndex = 0;
          activateKonamiEasterEgg();
        }
      } else {
        // Reset if wrong key pressed
        konamiIndex = 0;
      }
    };

    const activateKonamiEasterEgg = () => {
      // Rainbow effect on page
      document.body.classList.add('konami-activated');
      
      // Console message
      console.log('%c🎮 KONAMI CODE ACTIVATED! 🎮', 'font-size: 24px; color: #ff00ff; font-weight: bold; text-shadow: 2px 2px #00ffff;');
      console.log('%c═══════════════════════════════════════', 'color: #ff00ff;');
      console.log('%c🏆 Achievement Unlocked: Code Master', 'font-size: 16px; color: #00ff00;');
      console.log('%c', 'padding: 100px 200px; background: linear-gradient(45deg, #ff0000, #ff7f00, #ffff00, #00ff00, #0000ff, #4b0082, #8f00ff); background-size: 400% 400%; animation: gradient 3s ease infinite;');
      console.log('%c', 'padding: 0;');
      console.log('%cSECRET PATHS REVEALED:', 'font-size: 14px; color: #ff6600; font-weight: bold;');
      console.log('  🔐 Admin Login: /admin/login');
      console.log('  📊 Debug Config: /api/debug/config?debug=true&show_secrets=true');
      console.log('  👥 Internal Employees: /api/internal/employees (use X-API-Key: NGS-API-TEST-2024-xK9mP2nL)');
      console.log('  💾 User Backup: /api/backup/users');
      console.log('  🔑 Secrets: /api/internal/secrets?api_key=NGS-API-TEST-2024-xK9mP2nL');
      console.log('%c═══════════════════════════════════════', 'color: #ff00ff;');
      console.log('%cFlag: NGS-CTF-FLAG-{K0N4M1_C0D3_M4ST3R}', 'font-size: 12px; color: #00ffff; background: #000; padding: 5px;');
      
      // Show alert with Easter egg info
      alert(`🎮 KONAMI CODE ACTIVATED! 🎮

You found a hidden Easter egg!

Secret paths have been logged to the console.
Press F12 to see them.

Flag: NGS-CTF-FLAG-{K0N4M1_C0D3_M4ST3R}

Hint: Try these endpoints:
- /admin/login
- /api/debug/config?debug=true
- /api/internal/employees`);
      
      // Remove rainbow effect after 5 seconds
      setTimeout(() => {
        document.body.classList.remove('konami-activated');
      }, 5000);
    };

    // ============================================================
    // OSINT Easter Egg - Debug Panel (Ctrl+Shift+D)
    // ============================================================
    const handleDebugPanel = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.shiftKey && event.code === 'KeyD') {
        event.preventDefault();
        const debugPanel = document.getElementById('debug-panel');
        if (debugPanel) {
          debugPanel.classList.toggle('active');
          if (debugPanel.classList.contains('active')) {
            debugPanel.innerHTML = `
              <div style="font-family: monospace; color: #00ff00;">
                <h3 style="margin: 0 0 10px 0; color: #ff6600;">🔧 DEBUG PANEL</h3>
                <p>Version: 2.1.3</p>
                <p>Build: NGS-BUILD-4521</p>
                <p>Environment: production</p>
                <hr style="border-color: #00ff00; margin: 10px 0;">
                <p style="color: #ff0000;">⚠️ INTERNAL USE ONLY</p>
                <p>API: /api</p>
                <p>Internal: /api/internal</p>
                <p>Debug: /api/debug/config</p>
                <p>API Key: NGS-API-TEST-2024-xK9mP2nL</p>
                <hr style="border-color: #00ff00; margin: 10px 0;">
                <p>Session: ${localStorage.getItem('nexus_session_id') || 'None'}</p>
                <p>Tracking: Enabled</p>
                <hr style="border-color: #00ff00; margin: 10px 0;">
                <p style="color: #00ffff;">Press Ctrl+Shift+D to close</p>
              </div>
            `;
            console.log('🔧 Debug panel activated');
            console.log('Flag: NGS-CTF-FLAG-{D3BUG_P4N3L}');
          }
        }
      }
    };

    // ============================================================
    // OSINT Easter Egg - Secret Command Input
    // ============================================================
    let secretBuffer = '';
    const handleSecretCommand = (event: KeyboardEvent) => {
      // Only track alphanumeric keys
      if (event.key.length === 1) {
        secretBuffer += event.key.toLowerCase();
        
        // Keep buffer to reasonable size
        if (secretBuffer.length > 20) {
          secretBuffer = secretBuffer.slice(-20);
        }
        
        // Check for secret commands
        if (secretBuffer.includes('nexus')) {
          console.log('%c🔍 Nexus keyword detected! Try: nexusDebug.hint()', 'color: #00ff00;');
          secretBuffer = '';
        }
        
        if (secretBuffer.includes('admin')) {
          console.log('%c🔐 Admin access at: /admin/login', 'color: #ff6600;');
          secretBuffer = '';
        }
        
        if (secretBuffer.includes('secret')) {
          console.log('%c🤫 Secrets at: /api/internal/secrets?api_key=NGS-API-TEST-2024-xK9mP2nL', 'color: #ff00ff;');
          secretBuffer = '';
        }
        
        if (secretBuffer.includes('debug')) {
          console.log('%c🔧 Debug endpoint: /api/debug/config?debug=true&show_secrets=true', 'color: #00ffff;');
          secretBuffer = '';
        }
      }
    };

    // Register event listeners
    window.addEventListener('keydown', handleKonamiCode);
    window.addEventListener('keydown', handleDebugPanel);
    window.addEventListener('keydown', handleSecretCommand);
    
    // Cleanup
    return () => {
      window.removeEventListener('keydown', handleKonamiCode);
      window.removeEventListener('keydown', handleDebugPanel);
      window.removeEventListener('keydown', handleSecretCommand);
    };
  }, []);

  return (
    <>
      <Helmet>
        <html lang="en" />
        <title>Nexus Global Solutions - International Business Consulting</title>
        <meta name="description" content="Leading international business consulting firm specializing in emerging markets and strategic partnerships." />
      </Helmet>
      
      <RouteTracker />
      
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={
          <Layout>
            <HomePage />
          </Layout>
        } />
        <Route path="/about" element={
          <Layout>
            <AboutPage />
          </Layout>
        } />
        <Route path="/about/leadership" element={
          <Layout>
            <LeadershipPage />
          </Layout>
        } />
        
        {/* Employee Directory */}
        <Route path="/employees" element={
          <Layout>
            <EmployeesPage />
          </Layout>
        } />
        <Route path="/employees/:id" element={
          <Layout>
            <EmployeeDetailPage />
          </Layout>
        } />
        
        {/* News & Communications */}
        <Route path="/news" element={
          <Layout>
            <NewsPage />
          </Layout>
        } />
        <Route path="/news/:slug" element={
          <Layout>
            <NewsDetailPage />
          </Layout>
        } />
        
        {/* Documents */}
        <Route path="/documents" element={
          <Layout>
            <DocumentsPage />
          </Layout>
        } />
        <Route path="/documents/:category" element={
          <Layout>
            <DocumentsPage />
          </Layout>
        } />
        
        {/* Contact & Business */}
        <Route path="/contact" element={
          <Layout>
            <ContactPage />
          </Layout>
        } />
        <Route path="/contact/media" element={
          <Layout>
            <MediaContactPage />
          </Layout>
        } />
        <Route path="/careers" element={
          <Layout>
            <CareersPage />
          </Layout>
        } />
        <Route path="/partnerships" element={
          <Layout>
            <PartnershipsPage />
          </Layout>
        } />
        
        {/* Services Routes */}
        <Route path="/services/:service" element={
          <Layout>
            <ServicesPage />
          </Layout>
        } />

        {/* Admin Routes */}
        <Route path="/admin/login" element={<LoginPage />} />
        <Route path="/admin/dashboard" element={
          <ProtectedRoute>
            <AdminLayout>
              <DashboardPage />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin" element={
          <ProtectedRoute>
            <AdminLayout>
              <DashboardPage />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/sessions" element={
          <ProtectedRoute>
            <AdminLayout>
              <SessionsPage />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/logs" element={
          <ProtectedRoute>
            <AdminLayout>
              <LogsPage />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/settings" element={
          <ProtectedRoute>
            <AdminLayout>
              <SettingsPage />
            </AdminLayout>
          </ProtectedRoute>
        } />
        
        {/* 404 Page */}
        <Route path="*" element={
          <Layout>
            <NotFoundPage />
          </Layout>
        } />
      </Routes>
    </>
  )
}

export default App
